import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, Settings, Sun, Moon, Upload, Wifi } from 'lucide-react';
import io from 'socket.io-client';
import apiClient from '../services/apiClient';
import authService from '../services/authService';
import { FONT, LIGHT, DARK, btnStyle } from './card-ingest/theme';
import TabBar from './card-ingest/TabBar';
import EngineSettingsPanel from './card-ingest/EngineSettingsPanel';
import WirelessReceiverPanel from './card-ingest/WirelessReceiverPanel';
import RemoteConnectionsPanel from './card-ingest/RemoteConnectionsPanel';
import DestinationPresetsPanel from './card-ingest/DestinationPresetsPanel';
import AutomationsPanel from './card-ingest/AutomationsPanel';
import NewIngestForm from './card-ingest/NewIngestForm';
import IngestionRecordsList from './card-ingest/IngestionRecordsList';
import ScanAndOffload from './card-ingest/ScanAndOffload';
import CompletionView from './card-ingest/CompletionView';

/**
 * CardIngestWindow — the Electron card-ingest control window's React tree.
 *
 * Loaded the same way CompanionWindow.jsx is: the main app bundle with a
 * `?window=card-ingest` query param bypassing the router (see src/index.js).
 *
 * Flow: detected removable volumes list -> pick one -> scan (camera profile
 * + file count/size) -> pick a destination folder -> start a checksum-
 * verified offload -> live progress -> completion summary. Never deletes or
 * modifies anything on the source card at any point.
 *
 * This file owns state/orchestration only -- every screen is a component
 * under ./card-ingest/ (theme.js has the shared palette/button styles, and
 * ./card-ingest/progress/ has the real per-file + overall progress display
 * that used to be a single inline blob here).
 */
const THEME_STORAGE_KEY = 'cardIngestTheme'; // 'light' | 'dark' | absent = follow OS

const CardIngestWindow = () => {
  // Manual override takes priority; falls back to OS preference and keeps
  // tracking it live until the user explicitly picks a theme via the header
  // toggle, at which point it's pinned and persisted.
  const [themeOverride, setThemeOverride] = useState(() => {
    try { return localStorage.getItem(THEME_STORAGE_KEY) || null; } catch { return null; }
  });
  const [osIsDark, setOsIsDark] = useState(
    () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setOsIsDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const isDarkMode = themeOverride ? themeOverride === 'dark' : osIsDark;
  const toggleTheme = useCallback(() => {
    const next = isDarkMode ? 'light' : 'dark';
    setThemeOverride(next);
    try { localStorage.setItem(THEME_STORAGE_KEY, next); } catch { /* private/incognito storage block — theme just won't persist */ }
  }, [isDarkMode]);
  const c = isDarkMode ? DARK : LIGHT;

  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  // Declared here (rather than alongside remoteConnections/automationProfiles
  // further down) because startFromAutomation's useCallback below references
  // it in its dependency array -- a const declared later in this same
  // function body would still be in the temporal dead zone when that
  // useCallback call is evaluated during render, throwing a ReferenceError.
  const [destinationPresets, setDestinationPresets] = useState([]);
  // Defaults to the whole mounted volume, but overridable -- a detected
  // "removable" device isn't always a dedicated camera card; it could be a
  // full external hard disk, where scanning/ingesting everything under the
  // mount root would sweep in far more than intended.
  const [sourcePath, setSourcePath] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [destPath, setDestPath] = useState('');
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [offloading, setOffloading] = useState(false);

  // Project templates (Phase 3) — entirely user-defined, nothing pre-seeded.
  // Selecting a template switches the destination step from "pick any
  // folder" to "pick a project root + fill in this template's variables".
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [projectRoot, setProjectRoot] = useState('');
  const [templateVariables, setTemplateVariables] = useState({});
  const [resolvedPath, setResolvedPath] = useState('');
  const [resolving, setResolving] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find(t => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );
  const effectiveDestPath = selectedTemplate ? resolvedPath : destPath;

  // A blank {VAR} silently resolves to a literal "untitled" folder (see
  // sanitizePathSegment in card-ingest.js) -- that's how a real ingest ended
  // up nested under "untitled/untitled". Block starting the ingest until
  // every variable this template defines has a real value, instead of
  // letting it through and building a nonsense path.
  const missingVariables = useMemo(() => {
    if (!selectedTemplate) return [];
    return selectedTemplate.variableNames.filter(name => !String(templateVariables[name] || '').trim());
  }, [selectedTemplate, templateVariables]);

  const loadTemplates = useCallback(async () => {
    const result = await window.electronAPI.cardIngest.listTemplates();
    if (result?.success) setTemplates(result.data);
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // Seed the list, then stay live via the main process's poll-based watcher.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await window.electronAPI.cardIngest.listDevices();
      if (!cancelled && result?.success) setDevices(result.data);
    })();
    const offDetected = window.electronAPI.cardIngest.onDetected((drive) => {
      setDevices((prev) => (prev.some(d => d.device === drive.device) ? prev : [...prev, drive]));
    });
    const offRemoved = window.electronAPI.cardIngest.onRemoved((drive) => {
      setDevices((prev) => prev.filter(d => d.device !== drive.device));
      setSelectedDevice((sel) => (sel?.device === drive.device ? null : sel));
    });
    return () => { cancelled = true; offDetected(); offRemoved(); };
  }, []);

  // Live path preview — debounced so typing in a variable field doesn't
  // fire an IPC round-trip on every keystroke.
  const resolveTimer = useRef(null);
  useEffect(() => {
    if (!selectedTemplate || !projectRoot) {
      setResolvedPath('');
      return;
    }
    setResolving(true);
    if (resolveTimer.current) clearTimeout(resolveTimer.current);
    resolveTimer.current = setTimeout(async () => {
      const result = await window.electronAPI.cardIngest.resolveTemplate({
        rootPath: projectRoot, templateId: selectedTemplate.id, variables: templateVariables,
      });
      if (result?.success) setResolvedPath(result.data.resolvedPath);
      setResolving(false);
    }, 250);
    return () => { if (resolveTimer.current) clearTimeout(resolveTimer.current); };
  }, [selectedTemplate, projectRoot, templateVariables]);

  // Smart prefill: recognized variable names get sensible defaults from the
  // scanned card, but any other {VAR} the user's template defines is just a
  // plain empty text field — the template shape is fully user-defined.
  useEffect(() => {
    if (!selectedTemplate || !scanResult || !selectedDevice) return;
    const today = new Date().toISOString().slice(0, 10);
    const defaults = {
      CAMERA: scanResult.cameraProfile.family !== 'Unknown' ? scanResult.cameraProfile.family : '',
      CARD_LABEL: selectedDevice.label || selectedDevice.description || '',
      DATE: today,
    };
    setTemplateVariables((prev) => {
      const next = { ...prev };
      for (const name of selectedTemplate.variableNames) {
        if (next[name] === undefined && defaults[name] !== undefined) next[name] = defaults[name];
        else if (next[name] === undefined) next[name] = '';
      }
      return next;
    });
  }, [selectedTemplate, scanResult, selectedDevice]);

  useEffect(() => {
    const offProgress = window.electronAPI.cardIngest.onProgress((data) => {
      if (data.jobId === jobId) setProgress(data);
    });
    const offComplete = window.electronAPI.cardIngest.onComplete((data) => {
      if (data.jobId === jobId) {
        setManifest(data);
        setOffloading(false);
      }
    });
    return () => { offProgress(); offComplete(); };
  }, [jobId]);

  // Returns the scan result directly (not just via setScanResult) so a
  // caller that needs to act on it immediately after (like running an
  // automation right after auto-selecting a device) doesn't hit a
  // stale-closure race — a useCallback made on a prior render still closes
  // over the OLD scanResult until the component actually re-renders, which
  // hasn't happened yet in the middle of one synchronous handler.
  const runScan = useCallback(async (scanPath) => {
    setScanResult(null);
    setScanError(null);
    setScanning(true);
    try {
      const result = await window.electronAPI.cardIngest.scan(scanPath);
      if (result?.success) { setScanResult(result.data); return result.data; }
      setScanError(result?.error || 'Scan failed');
      return null;
    } catch (err) {
      setScanError(err.message);
      return null;
    } finally {
      setScanning(false);
    }
  }, []);

  // The job-history record for a detected card already exists (created
  // 'queued' the moment card-ingest.js saw it) -- carry its jobId through so
  // starting the offload UPDATES that same list row instead of minting a
  // second, disconnected one.
  const [pendingJobId, setPendingJobId] = useState(null);

  // IngestionRecordsList owns its own listJobs() poll, independent of this
  // component's state -- bumping this forces its effect to refire on demand
  // (job just created here) rather than waiting up to 4s for the next poll.
  const [jobsRefreshTick, setJobsRefreshTick] = useState(0);
  const bumpJobsRefresh = useCallback(() => setJobsRefreshTick((t) => t + 1), []);

  const selectDevice = useCallback(async (device) => {
    setSelectedDevice(device);
    setPendingJobId(device.jobId || null);
    setSourcePath(device.mountPath);
    setManifest(null);
    setProgress(null);
    setDestPath('');
    setSelectedTemplateId(null);
    setProjectRoot('');
    setTemplateVariables({});
    setResolvedPath('');
    return runScan(device.mountPath);
  }, [runScan]);

  // Pulling FROM a remote FTP/SFTP server instead of a physical card -- no
  // "device" actually exists, so a synthetic one stands in wherever the UI
  // (ScanAndOffload, the ingestion list) expects device.description/label,
  // and remoteSourceConnection carries the real connection through to
  // startOffload. mountPath stays null, which ScanAndOffload uses to hide
  // the "narrow to folder" control (that's a local-mount-only concept).
  const [remoteSourceConnection, setRemoteSourceConnection] = useState(null);
  const selectRemoteSource = useCallback(async (connection) => {
    const syntheticDevice = { device: `remote:${connection.id}`, description: connection.name, label: connection.name, mountPath: null };
    setSelectedDevice(syntheticDevice);
    setRemoteSourceConnection(connection);
    setPendingJobId(null);
    setSourcePath(`${connection.protocol}://${connection.host}${connection.rootPath || '/'}`);
    setManifest(null);
    setProgress(null);
    setDestPath('');
    setSelectedTemplateId(null);
    setProjectRoot('');
    setTemplateVariables({});
    setResolvedPath('');
    setScanResult(null);
    setScanError(null);
    setScanning(true);
    try {
      const result = await window.electronAPI.cardIngest.scanRemoteSource(connection);
      if (result?.success) setScanResult(result.data);
      else setScanError(result?.error || 'Scan failed');
    } catch (err) {
      setScanError(err.message);
    } finally {
      setScanning(false);
    }
  }, []);

  // Narrows ingest to a specific folder instead of the whole mounted
  // volume — important when the detected device turns out to be a full
  // external hard disk rather than a dedicated camera card. Starts the
  // picker inside the device's mount path rather than some arbitrary
  // default location.
  const chooseSourceFolder = useCallback(async () => {
    const result = await window.electronAPI.cardIngest.chooseSourceFolder(sourcePath);
    if (result?.success && result.path) {
      setSourcePath(result.path);
      await runScan(result.path);
    }
  }, [sourcePath, runScan]);

  const chooseDestination = useCallback(async () => {
    const result = await window.electronAPI.cardIngest.chooseDestination();
    if (result?.success && result.path) setDestPath(result.path);
  }, []);

  const createFolderIngest = useCallback(async ({ sourcePath: folder, destination, operation }) => {
    const created = await window.electronAPI.cardIngest.createJob({
      sourcePath: folder, destPath: destination, operation,
    });
    if (!created?.success) return created;
    const queued = created.data;
    // 'proxy_only' and 'copy_and_proxy' below never set selectedDevice, so
    // the UI stays right here on the records list -- without this nudge,
    // IngestionRecordsList (a sibling, with its own independent listJobs()
    // poll) has no idea a job just got created and wouldn't show it until
    // its next 4s tick.
    bumpJobsRefresh();
    if (operation === 'proxy_only') {
      setJobId(queued.jobId);
      return window.electronAPI.cardIngest.startProxyOnly({
        jobId: queued.jobId, sourcePath: folder, proxyRoot: destination,
      });
    }
    if (operation === 'copy_and_proxy') {
      setJobId(queued.jobId);
      setProgress(null);
      setOffloading(true);
      const result = await window.electronAPI.cardIngest.startOffload({
        jobId: queued.jobId,
        sourcePath: folder,
        destPath: destination,
        operation,
      });
      setOffloading(false);
      if (result?.success) setManifest(result.data);
      return result;
    }
    const device = {
      device: `folder:${queued.jobId}`, jobId: queued.jobId,
      description: queued.deviceLabel, label: queued.deviceLabel,
      mountPath: folder, sourceType: 'folder',
    };
    setSelectedDevice(device);
    setPendingJobId(queued.jobId);
    setSourcePath(folder);
    setDestPath(destination);
    setManifest(null);
    setProgress(null);
    setSelectedTemplateId(null);
    const scan = await runScan(folder);
    return scan ? { success: true } : { success: false, error: 'Scan failed' };
  }, [runScan]);

  const chooseProjectRoot = useCallback(async () => {
    const result = await window.electronAPI.cardIngest.chooseProjectRoot();
    if (result?.success && result.path) setProjectRoot(result.path);
  }, []);

  const setTemplateVariable = useCallback((name, value) => {
    setTemplateVariables((prev) => ({ ...prev, [name]: value }));
  }, []);

  const createTemplate = useCallback(async (name, pattern) => {
    const result = await window.electronAPI.cardIngest.saveTemplate({ name, pattern });
    if (result?.success) {
      await loadTemplates();
      setSelectedTemplateId(result.data.id);
    }
    return result;
  }, [loadTemplates]);

  const deleteTemplateHandler = useCallback(async (templateId) => {
    await window.electronAPI.cardIngest.deleteTemplate(templateId);
    if (selectedTemplateId === templateId) setSelectedTemplateId(null);
    await loadTemplates();
  }, [selectedTemplateId, loadTemplates]);

  // Which saved remote connections (if any) offloaded footage also gets
  // pushed to, ALL of them at once for the same ingest — a shoot commonly
  // needs both an on-set backup server and an off-site editorial server for
  // the same card. Declared here (before startIngest, which reads it)
  // rather than down with the other show*/panel state below — startIngest's
  // useCallback dependency array and body both reference it during render,
  // so it must already be initialized by the time that useCallback call is
  // evaluated, not just by the time startIngest is actually invoked.
  const [selectedConnectionIds, setSelectedConnectionIds] = useState([]);
  const toggleConnectionId = useCallback((id) => {
    setSelectedConnectionIds((prev) => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  }, []);

  // `overrides` lets an automation profile's one-click confirm start the
  // transfer immediately with a just-resolved path, instead of setting state
  // and waiting a render cycle for the debounced resolveTemplate effect (and
  // for effectiveDestPath/missingVariables, which only reflect state set on
  // a *previous* render) to catch up.
  const startIngest = useCallback(async (overrides = {}) => {
    const effectiveDest = overrides.destPath || effectiveDestPath;
    const effectiveRoot = overrides.projectRoot !== undefined ? overrides.projectRoot : (selectedTemplate ? projectRoot : undefined);
    const connIds = overrides.connectionIds !== undefined ? overrides.connectionIds : selectedConnectionIds;
    if (!sourcePath || !effectiveDest || (!overrides.destPath && missingVariables.length > 0)) return;
    const newJobId = overrides.jobId || pendingJobId || crypto.randomUUID();
    setJobId(newJobId);

    // Proxy-only never copies the source footage -- route straight to the
    // proxy-only IPC channel instead of startOffload below, which always
    // performs an actual file copy regardless of `operation` (see its
    // main.js handler -- `operation` is only read afterward to decide
    // whether to ALSO enqueue proxy generation for copy_and_proxy). Without
    // this branch, picking "Proxy only" here silently behaved exactly like
    // "Verified copy". Mirrors the same branch already correct in
    // startFromAutomation/createFolderIngest above.
    if (overrides.operation === 'proxy_only') {
      return window.electronAPI.cardIngest.startProxyOnly({
        jobId: newJobId, sourcePath, proxyRoot: effectiveDest,
        proxyDeliveryTargets: overrides.proxyDeliveryTargets || [],
      });
    }

    setProgress(null);
    setManifest(null);
    setOffloading(true);

    // Fetch the DECRYPTED credentials for every selected connection right
    // before use, not earlier/cached — see server/routes/ftpConnections.js's
    // GET /:id/credentials docstring. A connection whose credential fetch
    // fails is just dropped (logged), not fatal to the whole job.
    const remoteConnections = (await Promise.all(
      (connIds || []).map(async (id) => {
        try {
          return await apiClient.get(`/api/ftp-connections/${id}/credentials`);
        } catch (err) {
          console.error('[CardIngest] Failed to fetch connection credentials, skipping:', err);
          return null;
        }
      })
    )).filter(Boolean);

    // remoteSourceConnection (list state) only carries the PUBLIC shape (no
    // password) -- fetch decrypted credentials right before use, same as
    // the upload connections above, not earlier/cached.
    let remoteSource;
    if (remoteSourceConnection) {
      try {
        remoteSource = await apiClient.get(`/api/ftp-connections/${remoteSourceConnection.id}/credentials`);
      } catch (err) {
        console.error('[CardIngest] Failed to fetch source connection credentials:', err);
        setOffloading(false);
        return;
      }
    }

    const result = await window.electronAPI.cardIngest.startOffload({
      jobId: newJobId, sourcePath, destPath: effectiveDest,
      projectRoot: effectiveRoot,
      operation: overrides.operation || 'copy',
      remoteConnections,
      remoteSource,
      profileName: overrides.profileName,
      proxyDeliveryTargets: overrides.proxyDeliveryTargets || [],
      masterDeliveryTargets: overrides.masterDeliveryTargets || [],
    });
    if (result?.success) {
      setManifest(result.data);
    }
    setOffloading(false);
  }, [sourcePath, effectiveDestPath, selectedTemplate, projectRoot, missingVariables, selectedConnectionIds, pendingJobId, remoteSourceConnection]);

  // One-click confirm for a matched automation profile — pre-fills the same
  // fields the manual flow uses (so if the user backs out, everything they'd
  // see is consistent with what actually started), resolves the destination
  // path directly rather than through the debounced preview effect, and
  // starts the transfer immediately. Accepts explicit scanResult/device
  // overrides for the case where a device was JUST auto-selected in the same
  // gesture (see runAutomationOnDevice below) — reading the closed-over
  // state there would still be the pre-selection value until the next
  // render, not what was just scanned.
  const startFromAutomation = useCallback(async (profile, overrides = {}) => {
    const effectiveScanResult = overrides.scanResult !== undefined ? overrides.scanResult : scanResult;
    const effectiveDevice = overrides.device !== undefined ? overrides.device : selectedDevice;
    // Newer profiles reference a saved Destination preset by id; older ones
    // (saved before Destinations was split into its own module) still carry
    // this config inline on the profile itself -- resolve whichever is present.
    const destination = profile.destinationId
      ? destinationPresets.find(d => d.id === profile.destinationId) || null
      : profile;
    const template = templates.find(t => t.id === destination?.templateId) || null;
    const variables = { ...(destination?.templateVariables || {}) };
    if (template) {
      const today = new Date().toISOString().slice(0, 10);
      for (const name of template.variableNames) {
        if (variables[name] !== undefined) continue;
        if (/date/i.test(name)) variables[name] = today;
        else if (name === 'CAMERA') variables[name] = effectiveScanResult?.cameraProfile?.family && effectiveScanResult.cameraProfile.family !== 'Unknown' ? effectiveScanResult.cameraProfile.family : '';
        else if (name === 'CARD_LABEL') variables[name] = effectiveDevice?.label || effectiveDevice?.description || '';
        else variables[name] = '';
      }
    }
    setSelectedTemplateId(destination?.templateId || null);
    setProjectRoot(destination?.rootPath || '');
    setTemplateVariables(variables);

    let destPathOverride = destination?.rootPath || undefined;
    if (template && destination?.rootPath) {
      const resolved = await window.electronAPI.cardIngest.resolveTemplate({
        rootPath: destination.rootPath, templateId: destination.templateId, variables,
      });
      if (resolved?.success) {
        setResolvedPath(resolved.data.resolvedPath);
        destPathOverride = resolved.data.resolvedPath;
      }
    }

    const remoteTargets = destination?.remoteTargets
      || (destination?.remoteConnectionIds || []).map(connectionId => ({ connectionId, role: 'master_backup' }));
    const connIds = remoteTargets.filter(target => target.role === 'master_backup').map(target => target.connectionId);
    const proxyDeliveryTargets = remoteTargets.filter(target => target.role === 'proxy_delivery');
    const masterDeliveryTargets = [];
    if (destination?.autoUploadDrive && destination?.driveMappingId && destination?.driveRole === 'proxy_delivery') {
      proxyDeliveryTargets.push({ type: 'drive', mappingId: destination.driveMappingId, role: 'proxy_delivery' });
    } else if (destination?.autoUploadDrive && destination?.driveMappingId) {
      masterDeliveryTargets.push({ type: 'drive', mappingId: destination.driveMappingId, role: 'master_backup' });
    }
    setSelectedConnectionIds(connIds);
    setMatchedProfile(null);

    if (destination?.operation === 'proxy_only' && destPathOverride && sourcePath) {
      const proxyJobId = overrides.jobId || pendingJobId || crypto.randomUUID();
      setJobId(proxyJobId);
      await window.electronAPI.cardIngest.startProxyOnly({
        jobId: proxyJobId, sourcePath, proxyRoot: destPathOverride,
        proxyDeliveryTargets,
      });
      return;
    }

    await startIngest({
      destPath: destPathOverride,
      projectRoot: template ? destination.rootPath : undefined,
      connectionIds: connIds,
      profileName: profile.name,
      jobId: overrides.jobId,
      operation: destination?.operation || 'copy',
      proxyDeliveryTargets,
      masterDeliveryTargets,
    });
  }, [templates, scanResult, selectedDevice, startIngest, destinationPresets, sourcePath, pendingJobId]);

  // The actual "just click Run" entry point from the Ingest landing screen:
  // select + scan a device and kick off its matched automation in one
  // gesture, instead of making the user open the device first (where the
  // matched-profile banner used to be buried) and THEN click Start.
  const runAutomationOnDevice = useCallback(async (device, profile) => {
    setSelectedDevice(device);
    setPendingJobId(device.jobId || null);
    setSourcePath(device.mountPath);
    setManifest(null);
    setProgress(null);
    setDestPath('');
    setSelectedTemplateId(null);
    setProjectRoot('');
    setTemplateVariables({});
    setResolvedPath('');
    const scan = await runScan(device.mountPath);
    await startFromAutomation(profile, { scanResult: scan, device, jobId: device.jobId });
  }, [runScan, startFromAutomation]);

  const cancelIngest = useCallback(async () => {
    if (jobId) await window.electronAPI.cardIngest.cancelOffload(jobId);
  }, [jobId]);

  const reset = useCallback(() => {
    setSelectedDevice(null);
    setPendingJobId(null);
    setRemoteSourceConnection(null);
    setSourcePath('');
    setScanResult(null);
    setScanError(null);
    setDestPath('');
    setSelectedTemplateId(null);
    setProjectRoot('');
    setTemplateVariables({});
    setResolvedPath('');
    setJobId(null);
    setProgress(null);
    setManifest(null);
    setOffloading(false);
  }, []);

  const progressPct = useMemo(() => {
    if (!progress || !progress.totalBytes) return 0;
    return Math.min(100, Math.round((progress.bytesDone / progress.totalBytes) * 100));
  }, [progress]);

  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('ingest');
  // The Upload tab covers two opposite-direction actions that shouldn't be
  // stacked together as one undifferentiated list: pushing footage OUT to a
  // saved server (used during ingest) vs. a camera pushing footage IN to us
  // over WiFi (the wireless receiver is itself a small FTP server). Picking
  // one narrows the tab to exactly the config for that direction.
  const [uploadDirection, setUploadDirection] = useState('send');
  const [remoteConnections, setRemoteConnections] = useState([]);
  const [automationProfiles, setAutomationProfiles] = useState([]);
  // Set when a just-detected card's volume label matches a saved automation
  // profile — surfaced as a pre-filled, one-click-confirm banner in
  // ScanAndOffload rather than auto-starting the transfer (see
  // card-ingest.js's matchAutomationProfile docstring for why).
  const [matchedProfile, setMatchedProfile] = useState(null);

  const loadRemoteConnections = useCallback(async () => {
    try {
      const list = await apiClient.get('/api/ftp-connections');
      setRemoteConnections(Array.isArray(list) ? list : (list?.data || []));
    } catch (err) {
      console.error('[CardIngest] Failed to load remote connections:', err);
    }
  }, []);

  const loadAutomationProfiles = useCallback(async () => {
    const result = await window.electronAPI.cardIngest.listAutomationProfiles();
    if (result?.success) setAutomationProfiles(result.data);
  }, []);

  const loadDestinationPresets = useCallback(async () => {
    const result = await window.electronAPI.cardIngest.listDestinationPresets();
    if (result?.success) setDestinationPresets(result.data);
  }, []);

  useEffect(() => {
    loadRemoteConnections(); loadAutomationProfiles(); loadDestinationPresets();
  }, [loadRemoteConnections, loadAutomationProfiles, loadDestinationPresets]);

  useEffect(() => {
    const offMatched = window.electronAPI.cardIngest.onProfileMatched(({ drive, profile }) => {
      // Only apply if this is the device currently selected/open — avoids
      // clobbering an in-progress unrelated ingest if a second card shows
      // up while the user is mid-setup with a different one.
      setMatchedProfile({ drive, profile });
    });
    return () => offMatched?.();
  }, []);

  // Read at trigger-fire time rather than closed over at socket-setup time,
  // since the socket connection below is only established once (or once per
  // login), while matchedProfile changes constantly as cards come and go.
  const matchedProfileRef = useRef(null);
  useEffect(() => { matchedProfileRef.current = matchedProfile; }, [matchedProfile]);

  // Remote trigger from mobile's Studio Tools "Companion Trigger" action
  // (see server/routes/companion.js POST /trigger-offload) — relayed over
  // the SAME /companion Socket.IO namespace the Premiere Assembler plugin
  // uses (server/sockets/companionFootage.js), just a different event.
  // Only acts if a card is already sitting with a matched automation profile
  // pending confirmation — never starts an offload the desktop hasn't
  // already detected and matched a profile for, matching the same
  // deliberate-confirm safety property as the local one-click banner (see
  // card-ingest.js's matchAutomationProfile docstring).
  useEffect(() => {
    const token = authService.getToken?.();

    let machineId;
    try {
      machineId = localStorage.getItem('cardIngestMachineId');
      if (!machineId) {
        machineId = crypto.randomUUID();
        localStorage.setItem('cardIngestMachineId', machineId);
      }
    } catch {
      machineId = crypto.randomUUID();
    }

    const socket = io(`${process.env.REACT_APP_SERVER_URL || 'http://localhost:3002'}/companion`, {
      withCredentials: true,
      auth: { ...(typeof token === 'string' ? { token } : {}), clientType: 'main-app', machineId },
      reconnection: true,
      reconnectionDelay: 2000,
    });
    socket.on('trigger-offload', () => {
      const pending = matchedProfileRef.current;
      if (!pending) return; // nothing detected/matched right now — no-op
      runAutomationOnDevice(pending.drive, pending.profile);
    });

    // Relay raw progress/completion to the server (-> mobile's Settings
    // screen), independent of the jobId-filtered onProgress/onComplete
    // subscription above that drives THIS window's own progress bar --
    // mobile wants "what's happening on this account right now" regardless
    // of which job the desktop UI happens to be looking at.
    const offRemoteProgress = window.electronAPI.cardIngest.onProgress((data) => {
      socket.emit('offload-progress', data);
    });
    const offRemoteComplete = window.electronAPI.cardIngest.onComplete((data) => {
      socket.emit('offload-complete', data);
    });

    return () => {
      offRemoteProgress();
      offRemoteComplete();
      socket.disconnect();
    };
  }, [runAutomationOnDevice]);

  return (
    <div style={{
      fontFamily: FONT, background: c.bg, color: c.text, height: '100vh',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px 0', flexShrink: 0,
      }}>
        <span style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em' }}>
          Offload <span style={{ fontWeight: 400, color: c.muted, fontSize: '11px' }}>by tarr.ink</span>
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {selectedDevice && (
            <button onClick={reset} style={btnStyle(c, 'ghost')}>
              <X size={12} /> Back to devices
            </button>
          )}
          <button onClick={toggleTheme} style={btnStyle(c, 'ghost')} title={isDarkMode ? 'Switch to light theme' : 'Switch to dark theme'}>
            {isDarkMode ? <Sun size={13} /> : <Moon size={13} />}
          </button>
          <button onClick={() => setShowSettings(true)} style={btnStyle(c, 'ghost')} title="Proxy engine settings">
            <Settings size={13} />
          </button>
        </div>
      </div>

      <TabBar c={c} activeTab={activeTab} setActiveTab={setActiveTab} />

      {showSettings && <EngineSettingsPanel c={c} onClose={() => setShowSettings(false)} />}

      <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
        {activeTab === 'ingest' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {selectedDevice && !manifest && (
              <ScanAndOffload
                c={c} device={selectedDevice} jobId={jobId} scanning={scanning} scanResult={scanResult} scanError={scanError}
                sourcePath={sourcePath} chooseSourceFolder={chooseSourceFolder}
                destPath={destPath} chooseDestination={chooseDestination}
                templates={templates} selectedTemplateId={selectedTemplateId} setSelectedTemplateId={setSelectedTemplateId}
                projectRoot={projectRoot} chooseProjectRoot={chooseProjectRoot}
                selectedTemplate={selectedTemplate} templateVariables={templateVariables} setTemplateVariable={setTemplateVariable}
                resolvedPath={resolvedPath} resolving={resolving}
                createTemplate={createTemplate} deleteTemplate={deleteTemplateHandler}
                effectiveDestPath={effectiveDestPath} missingVariables={missingVariables}
                offloading={offloading} progress={progress} progressPct={progressPct}
                startIngest={startIngest} cancelIngest={cancelIngest}
                remoteConnections={remoteConnections} selectedConnectionIds={selectedConnectionIds}
                toggleConnectionId={toggleConnectionId}
                matchedProfile={matchedProfile} startFromAutomation={startFromAutomation}
                dismissMatchedProfile={() => setMatchedProfile(null)}
              />
            )}

            {manifest && (
              <CompletionView c={c} manifest={manifest} onStartAnother={reset} />
            )}

            {!selectedDevice && !manifest && (
              <>
                <NewIngestForm c={c} onCreate={createFolderIngest} />
                <WirelessReceiverPanel c={c} embedded />
                <IngestionRecordsList
                  c={c} devices={devices} matchedProfile={matchedProfile}
                  runAutomationOnDevice={runAutomationOnDevice} onManualAssign={selectDevice}
                  destinationPresets={destinationPresets} remoteConnections={remoteConnections}
                  onSelectRemoteSource={selectRemoteSource}
                  liveJobId={jobId} liveProgress={progress} liveProgressPct={progressPct}
                  refreshTrigger={jobsRefreshTick}
                />
              </>
            )}
          </div>
        )}

        {activeTab === 'upload' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                onClick={() => setUploadDirection('send')}
                style={{ ...btnStyle(c, uploadDirection === 'send' ? 'primary' : 'ghost'), flex: 1, justifyContent: 'center' }}
              >
                <Upload size={13} /> Send to a server
              </button>
              {false && <button
                onClick={() => setUploadDirection('receive')}
                style={{ ...btnStyle(c, uploadDirection === 'receive' ? 'primary' : 'ghost'), flex: 1, justifyContent: 'center' }}
              >
                <Wifi size={13} /> Receive from a camera
              </button>}
            </div>

            {uploadDirection === 'send' && (
              <div>
                <div style={{ fontSize: '11px', color: c.muted, marginBottom: '12px', lineHeight: 1.5 }}>
                  Saved servers footage gets pushed to as it offloads off a card — connect these once here, then pick any number of them per ingest (or bake them into an automation profile so they're always included).
                </div>
                <RemoteConnectionsPanel c={c} connections={remoteConnections} onRefresh={loadRemoteConnections} embedded />
              </div>
            )}

            {false && uploadDirection === 'receive' && (
              <div>
                <div style={{ fontSize: '11px', color: c.muted, marginBottom: '12px', lineHeight: 1.5 }}>
                  The opposite direction — this machine runs a small FTP server so a camera can push clips to it directly over WiFi, no card involved.
                </div>
                <WirelessReceiverPanel c={c} embedded />
              </div>
            )}
          </div>
        )}

        {activeTab === 'destinations' && (
          <DestinationPresetsPanel
            c={c} presets={destinationPresets} templates={templates} remoteConnections={remoteConnections}
            onRefresh={loadDestinationPresets} embedded
          />
        )}

        {activeTab === 'automations' && (
          <AutomationsPanel
            c={c} profiles={automationProfiles} templates={templates} remoteConnections={remoteConnections}
            destinationPresets={destinationPresets}
            onRefresh={loadAutomationProfiles} embedded
          />
        )}
      </div>
    </div>
  );
};

export default CardIngestWindow;
